import React, { useState, useEffect, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import Drawer from 'react-modern-drawer';
import 'react-modern-drawer/dist/index.css';
import { Icon } from '@iconify/react/dist/iconify.js';
import { toast } from 'react-toastify';
import {
  getCountryAndLanguage,
  getProvincesByCountry,
  getDistrictsByProvince,
  getCommunesByDistrict,
  getVillagesByCommune,
  createGeographicalSoil,
  updateGeographicalSoil,
  getSoilMasterMetadata,
} from '../lib/api';

const LEVEL_NAMES = {
  1: "Country",
  2: "Province",
  3: "District",
  4: "Commune",
  5: "Village",
};

const DEFAULT_TEXTURES = [
  "Sand", "Loamy Sand", "Sandy Loam", "Loam", "Silt Loam", "Silt",
  "Sandy Clay Loam", "Clay Loam", "Silty Clay Loam", "Sandy Clay",
  "Silty Clay", "Clay",
];

/* ---------- Design tokens shared with AddPest drawer ---------- */
const customStyles = `
  .form-grid-container {
    display: grid !important;
    grid-template-columns: 1fr 1fr !important;
    gap: 1.5rem !important;
    width: 100% !important;
    margin-bottom: 1.25rem !important;
  }
  .form-grid-3 {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 1rem;
    width: 100%;
    margin-bottom: 1.25rem;
  }
  .form-grid-4 {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 1rem;
    width: 100%;
    margin-bottom: 1.25rem;
  }
  .form-control, textarea, .form-select {
    width: 100% !important;
    border-radius: 8px !important;
    padding: 8px 12px !important;
    border: 1px solid #ced4da !important;
    background: #fff !important;
    font-size: 0.9rem !important;
  }
  .form-select:disabled, .form-control:disabled {
    background: #f8fafc !important;
    cursor: not-allowed;
    color: #94a3b8;
  }
  .form-select:focus, .form-control:focus {
    border-color: #1a73e8 !important;
    box-shadow: 0 0 0 3px rgba(26, 115, 232, 0.15) !important;
  }
  .error-message { color: #dc3545; font-size: 0.8rem; margin-bottom: 1rem; padding: 8px 12px; background: #fee2e2; border-radius: 6px; }
  .success-message { color: #166534; text-align: center; margin-bottom: 1rem; padding: 8px 12px; background: #dcfce7; border-radius: 6px; }

  .drawer-content-scroll {
    height: calc(100vh - 40px);
    overflow-y: auto !important;
    padding-right: 10px;
  }
  .card-body {
    overflow-y: auto !important;
    max-height: 100vh !important;
    padding: 1.5rem !important;
    overscroll-behavior: contain !important;
  }

  /* Section cards */
  .soil-section {
    border: 1px solid #e2e8f0;
    border-radius: 10px;
    padding: 1rem 1.25rem;
    margin-bottom: 1.5rem;
    background: #ffffff;
  }
  .soil-section-title {
    font-weight: 600;
    font-size: 0.95rem;
    color: #1a73e8;
    margin-bottom: 1rem;
    display: flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
  }
  .soil-section-subtitle {
    font-size: 0.72rem;
    text-transform: uppercase;
    letter-spacing: 0.03em;
    color: #6b7280;
    font-weight: 700;
    margin: 0.5rem 0 0.6rem;
  }

  /* Target level chips */
  .soil-level-chips {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    margin-bottom: 1.25rem;
  }
  .soil-level-chip {
    padding: 8px 14px;
    border: 1px solid #e5e7eb;
    background: #f9fafb;
    border-radius: 8px;
    cursor: pointer;
    font-weight: 500;
    font-size: 0.82rem;
    display: flex;
    align-items: center;
    gap: 6px;
    transition: all 0.2s;
    user-select: none;
  }
  .soil-level-chip:hover { background: #f3f4f6; }
  .soil-level-chip.active {
    background: #e8f0fe;
    border-color: #1a73e8;
    color: #1a73e8;
    font-weight: 600;
  }
  .soil-level-chip input[type="radio"] {
    accent-color: #1a73e8;
    margin: 0;
  }

  /* Condition flag cards */
  .soil-flag-card {
    border: 1px solid #e5e7eb;
    border-radius: 8px;
    padding: 0.85rem 1rem 0.7rem;
    background: #f8fafc;
    display: flex;
    flex-direction: column;
    height: 100%;
    gap: 6px;
  }
  .soil-flag-head {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 6px;
  }
  .soil-flag-title {
    font-weight: 600;
    font-size: 0.85rem;
    color: #0f172a;
  }
  .soil-flag-desc {
    font-size: 0.72rem;
    color: #94a3b8;
    line-height: 1.4;
  }
  /* Segmented control */
  .soil-flag-segment {
    display: flex;
    margin-top: auto;
    border: 1px solid #e2e8f0;
    border-radius: 6px;
    overflow: hidden;
    background: #fff;
  }
  .soil-flag-segment button {
    flex: 1 1 0;
    padding: 5px 4px;
    font-size: 0.72rem;
    font-weight: 600;
    letter-spacing: 0.01em;
    background: transparent;
    border: none;
    border-right: 1px solid #e2e8f0;
    color: #64748b;
    cursor: pointer;
    transition: background .15s, color .15s;
  }
  .soil-flag-segment button:last-child { border-right: none; }
  .soil-flag-segment button:hover { background: #f1f5f9; color: #0f172a; }
  .soil-flag-segment button.active {
    background: #1a73e8;
    color: #fff;
  }
  .soil-flag-segment button.active.positive { background: #dc2626; }
  .soil-flag-segment button.active.negative { background: #64748b; }
  .soil-flag-hint {
    font-size: 0.68rem;
    color: #94a3b8;
    font-style: italic;
    margin-top: 4px;
  }
  .soil-flag-overridden-dot {
    display: inline-block;
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: #f59e0b;
    margin-left: 4px;
    vertical-align: middle;
  }

  /* Edit mode info panel */
  .soil-edit-panel {
    background: #f8fafc;
    border: 1px solid #e5e7eb;
    border-radius: 8px;
    padding: 1rem;
  }

  /* Footer buttons */
  .drawer-footer {
    display: flex;
    justify-content: center;
    gap: 12px;
    padding-top: 1rem;
    border-top: 1px solid #e5e7eb;
    margin-top: 0.5rem;
  }

  /* ---------- MultiSelect ---------- */
  .ms-trigger {
    width: 100%;
    min-height: 42px;
    border-radius: 8px;
    padding: 8px 12px;
    border: 1px solid #ced4da;
    background: #fff;
    font-size: 0.9rem;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    cursor: pointer !important;
    transition: border-color .15s, box-shadow .15s;
    user-select: none;
  }
  .ms-trigger * { cursor: pointer !important; }
  .ms-trigger:hover:not(.disabled) { border-color: #94a3b8; }
  .ms-trigger.open {
    border-color: #1a73e8;
    box-shadow: 0 0 0 3px rgba(26, 115, 232, 0.15);
  }
  .ms-trigger.disabled,
  .ms-trigger.disabled * {
    background: #f8fafc !important;
    cursor: not-allowed !important;
    color: #94a3b8;
  }
  .ms-value {
    flex: 1 1 auto;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    color: #0f172a;
  }
  .ms-value.placeholder { color: #94a3b8; }
  .ms-actions { display: flex; align-items: center; gap: 6px; flex-shrink: 0; }
  .ms-actions .spinner-border { pointer-events: none; }
  .ms-clear-btn {
    border: none;
    background: transparent;
    color: #94a3b8;
    padding: 0;
    line-height: 1;
    cursor: pointer;
  }
  .ms-clear-btn:hover { color: #dc3545; }

  /* Dropdown menu (portal) */
  .ms-menu {
    position: fixed;
    z-index: 2000;
    background: #fff;
    border: 1px solid #e2e8f0;
    border-radius: 10px;
    box-shadow: 0 10px 30px rgba(15, 23, 42, 0.18);
    padding: 8px;
    display: flex;
    flex-direction: column;
    max-height: 340px;
    animation: msFade .12s ease-out;
  }
  @keyframes msFade {
    from { opacity: 0; transform: translateY(-4px); }
    to { opacity: 1; transform: translateY(0); }
  }
  .ms-search-wrap { position: relative; margin-bottom: 6px; }
  .ms-search-wrap .icon-search {
    position: absolute; top: 50%; left: 10px;
    transform: translateY(-50%);
    color: #94a3b8;
    font-size: 16px;
    pointer-events: none;
  }
  .ms-search {
    width: 100% !important;
    padding: 6px 10px 6px 30px !important;
    font-size: 0.85rem !important;
    border-radius: 6px !important;
    border: 1px solid #e2e8f0 !important;
  }
  .ms-search:focus {
    border-color: #1a73e8 !important;
    outline: none;
    box-shadow: 0 0 0 2px rgba(26, 115, 232, 0.15);
  }
  .ms-toolbar {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 2px 4px 6px;
    font-size: 0.75rem;
    color: #64748b;
  }
  .ms-toolbar button {
    border: none; background: transparent;
    color: #1a73e8; font-weight: 600;
    padding: 0; cursor: pointer; font-size: 0.75rem;
  }
  .ms-toolbar button:disabled { color: #cbd5e1; cursor: not-allowed; }
  .ms-list {
    overflow-y: auto;
    flex: 1 1 auto;
    max-height: 230px;
    padding-right: 2px;
  }
  .ms-item {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 6px 8px;
    border-radius: 6px;
    cursor: pointer;
    font-size: 0.85rem;
    color: #0f172a;
  }
  .ms-item:hover { background: #f1f5f9; }
  .ms-item.selected { background: #e8f0fe; color: #1a3d7c; }
  .ms-item input[type="checkbox"] {
    accent-color: #1a73e8;
    margin: 0;
    flex-shrink: 0;
  }
  .ms-item-label { flex: 1 1 auto; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  .ms-empty {
    padding: 16px 8px;
    text-align: center;
    color: #94a3b8;
    font-size: 0.82rem;
  }

  /* Selected tags */
  .ms-tags {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    margin-top: 6px;
  }
  .ms-tag {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 3px 8px;
    border-radius: 6px;
    background: #f1f5f9;
    border: 1px solid #e2e8f0;
    font-size: 0.75rem;
    color: #334155;
    font-weight: 500;
  }
  .ms-tag button {
    border: none; background: transparent; padding: 0;
    color: #94a3b8; cursor: pointer; line-height: 1;
  }
  .ms-tag button:hover { color: #dc3545; }
`;

/* ============================================================
   Portal-based MultiSelect
   ============================================================ */
const MultiSelect = ({
  label,
  placeholder = "Select...",
  items = [],
  selectedIds = [],
  onChange,
  idKey = "ID",
  nameKey = "Name",
  disabled = false,
  loading = false,
  emptyMessage = "No items available",
  helperText,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [menuRect, setMenuRect] = useState(null);

  const triggerRef = useRef(null);
  const menuRef = useRef(null);

  const updateMenuRect = () => {
    if (!triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    const MENU_MAX_HEIGHT = 340;
    const spaceBelow = window.innerHeight - rect.bottom;
    const openUpward = spaceBelow < MENU_MAX_HEIGHT && rect.top > spaceBelow;

    setMenuRect({
      left: rect.left,
      width: rect.width,
      top: openUpward ? undefined : rect.bottom + 6,
      bottom: openUpward ? window.innerHeight - rect.top + 6 : undefined,
      openUpward,
    });
  };

  useEffect(() => {
    if (!isOpen) return;
    updateMenuRect();
    const handler = () => updateMenuRect();
    window.addEventListener("resize", handler);
    window.addEventListener("scroll", handler, true);
    return () => {
      window.removeEventListener("resize", handler);
      window.removeEventListener("scroll", handler, true);
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const handleClick = (e) => {
      if (
        triggerRef.current && !triggerRef.current.contains(e.target) &&
        menuRef.current && !menuRef.current.contains(e.target)
      ) setIsOpen(false);
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e) => { if (e.key === "Escape") setIsOpen(false); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [isOpen]);

  const filteredItems = useMemo(() => {
    if (!searchTerm.trim()) return items;
    const term = searchTerm.toLowerCase();
    return items.filter((it) => String(it[nameKey] || "").toLowerCase().includes(term));
  }, [items, searchTerm, nameKey]);

  const itemsMap = useMemo(() => {
    const map = new Map();
    items.forEach((it) => map.set(String(it[idKey]), it));
    return map;
  }, [items, idKey]);

  const allFilteredSelected = useMemo(
    () => filteredItems.length > 0 && filteredItems.every((it) => selectedIds.includes(it[idKey])),
    [filteredItems, selectedIds, idKey],
  );

  const toggleItem = (id) => {
    if (selectedIds.includes(id)) onChange(selectedIds.filter((x) => x !== id));
    else onChange([...selectedIds, id]);
  };

  const toggleSelectAll = () => {
    if (allFilteredSelected) {
      const set = new Set(filteredItems.map((it) => it[idKey]));
      onChange(selectedIds.filter((id) => !set.has(id)));
    } else {
      const set = new Set(selectedIds);
      filteredItems.forEach((it) => set.add(it[idKey]));
      onChange(Array.from(set));
    }
  };

  const clearAll = (e) => { e.stopPropagation(); onChange([]); };
  const removeTag = (id, e) => { e.stopPropagation(); onChange(selectedIds.filter((x) => x !== id)); };

  const renderTriggerValue = () => {
    if (loading) return "Loading...";
    if (selectedIds.length === 0) return placeholder;
    const names = selectedIds.slice(0, 2)
      .map((id) => itemsMap.get(String(id))?.[nameKey] || id)
      .join(", ");
    const extra = selectedIds.length > 2 ? ` +${selectedIds.length - 2} more` : "";
    return `${selectedIds.length} selected · ${names}${extra}`;
  };

  const openMenu = () => {
    if (disabled) return;
    setIsOpen((v) => !v);
  };

  return (
    <div style={{ position: "relative" }}>
      <div className="d-flex justify-content-between align-items-center mb-1">
        <label className="form-label fw-semibold mb-0" style={{ fontSize: "0.85rem", color: "#334155" }}>
          {label}
        </label>
        {selectedIds.length > 0 && (
          <span className="badge bg-primary-subtle text-primary fw-medium px-2 py-1" style={{ fontSize: "0.72rem" }}>
            {selectedIds.length} selected
          </span>
        )}
      </div>

      <div
        ref={triggerRef}
        className={`ms-trigger ${isOpen ? "open" : ""} ${disabled ? "disabled" : ""}`}
        onClick={openMenu}
        role="button"
        tabIndex={disabled ? -1 : 0}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") { e.preventDefault(); openMenu(); }
        }}
      >
        <span className={`ms-value ${selectedIds.length === 0 ? "placeholder" : ""}`}>
          {renderTriggerValue()}
        </span>
        <span className="ms-actions">
          {loading && <span className="spinner-border spinner-border-sm text-primary" role="status" />}
          {selectedIds.length > 0 && !disabled && (
            <button type="button" className="ms-clear-btn" onClick={clearAll} title="Clear all">
              <Icon icon="lucide:x" style={{ fontSize: 15 }} />
            </button>
          )}
          <Icon icon={isOpen ? "lucide:chevron-up" : "lucide:chevron-down"} style={{ fontSize: 18, color: "#94a3b8" }} />
        </span>
      </div>

      {helperText && (
        <small className="text-muted d-block mt-1" style={{ fontSize: "0.72rem" }}>{helperText}</small>
      )}

      {selectedIds.length > 0 && (
        <div className="ms-tags">
          {selectedIds.map((id) => (
            <span key={id} className="ms-tag">
              <span>{itemsMap.get(String(id))?.[nameKey] || id}</span>
              {!disabled && (
                <button type="button" onClick={(e) => removeTag(id, e)} title="Remove">
                  <Icon icon="lucide:x" style={{ fontSize: 12 }} />
                </button>
              )}
            </span>
          ))}
        </div>
      )}

      {isOpen && !disabled && menuRect &&
        createPortal(
          <div ref={menuRef} className="ms-menu"
            style={{ left: menuRect.left, width: menuRect.width, top: menuRect.top, bottom: menuRect.bottom }}>
            <div className="ms-search-wrap">
              <Icon icon="lucide:search" className="icon-search" />
              <input className="ms-search" type="text" placeholder="Search..."
                value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} autoFocus />
            </div>
            <div className="ms-toolbar">
              <button type="button" onClick={toggleSelectAll} disabled={filteredItems.length === 0}>
                {allFilteredSelected ? "Deselect all" : "Select all"}
              </button>
              <span>{filteredItems.length} of {items.length}</span>
            </div>
            <div className="ms-list">
              {filteredItems.length === 0 ? (
                <div className="ms-empty">{emptyMessage}</div>
              ) : (
                filteredItems.map((it) => {
                  const id = it[idKey];
                  const isSel = selectedIds.includes(id);
                  return (
                    <label key={id} className={`ms-item ${isSel ? "selected" : ""}`}>
                      <input type="checkbox" checked={isSel} onChange={() => toggleItem(id)} />
                      <span className="ms-item-label">{it[nameKey]}</span>
                    </label>
                  );
                })
              )}
            </div>
          </div>,
          document.body,
        )}
    </div>
  );
};

/* ============================================================
   Flag card with segmented control
   ============================================================ */
const ConditionFlagCard = ({
  title,
  description,
  autoValue,
  override,         // { enabled, value }
  onChange,         // (next) => void
  positiveLabel = "Yes",
  negativeLabel = "No",
  isManualOnly = false, // heavy metals -> no Auto option
}) => {
  const effectiveValue = override.enabled ? override.value : autoValue;
  const mode = override.enabled ? (override.value ? "yes" : "no") : "auto";

  return (
    <div className="soil-flag-card">
      <div className="soil-flag-head">
        <span className="soil-flag-title">{title}</span>
        <span className={`badge ${effectiveValue ? "bg-danger" : "bg-secondary"}`}>
          {effectiveValue ? "YES" : "NO"}
          {override.enabled && !isManualOnly && <span className="soil-flag-overridden-dot" title="Overridden" />}
        </span>
      </div>

      <div className="soil-flag-desc">{description}</div>

      <div className="soil-flag-segment" role="group" aria-label={`${title} value`}>
        {!isManualOnly && (
          <button
            type="button"
            className={mode === "auto" ? "active" : ""}
            onClick={() => onChange({ enabled: false, value: override.value })}
          >
            Auto
          </button>
        )}
        <button
          type="button"
          className={`${mode === "yes" ? "active positive" : ""}`}
          onClick={() => onChange({ enabled: true, value: true })}
        >
          {positiveLabel}
        </button>
        <button
          type="button"
          className={`${mode === "no" ? "active negative" : ""}`}
          onClick={() => onChange({ enabled: true, value: false })}
        >
          {negativeLabel}
        </button>
      </div>

      {override.enabled && !isManualOnly && (
        <div className="soil-flag-hint">
          Auto would be: {autoValue ? "YES" : "NO"}
        </div>
      )}
    </div>
  );
};

/* ============================================================
   Main Component
   ============================================================ */
const AddGeographicalSoil = ({
  isOpen,
  onClose,
  recordToEdit = null,
  onSuccess,
}) => {
  const getToken = () => localStorage.getItem("token") || localStorage.getItem("authToken");

  const [message, setMessage] = useState(null);
  const [texturesList, setTexturesList] = useState(DEFAULT_TEXTURES);

  const [countries, setCountries] = useState([]);
  const [selectedCountryId, setSelectedCountryId] = useState("");

  const [provinces, setProvinces] = useState([]);
  const [selectedProvinceIds, setSelectedProvinceIds] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [selectedDistrictIds, setSelectedDistrictIds] = useState([]);
  const [communes, setCommunes] = useState([]);
  const [selectedCommuneIds, setSelectedCommuneIds] = useState([]);
  const [villages, setVillages] = useState([]);
  const [selectedVillageIds, setSelectedVillageIds] = useState([]);

  const [loadingProvinces, setLoadingProvinces] = useState(false);
  const [loadingDistricts, setLoadingDistricts] = useState(false);
  const [loadingCommunes, setLoadingCommunes] = useState(false);
  const [loadingVillages, setLoadingVillages] = useState(false);

  const [targetLevel, setTargetLevel] = useState(2);

  const [formData, setFormData] = useState({
    texture: "Loam",
    bulk_density_g_cm3: "",
    ph: "6.5",
    ec_ds_m: "1.2",
    organic_carbon_pct: "0.75",
    cec_cmol_kg: "",
    esp_pct: "",
    available_n_kg_ha: "",
    available_p2o5_kg_ha: "",
    available_k2o_kg_ha: "",
    sulfur_s_mg_kg: "",
    calcium_cmol_kg: "",
    magnesium_cmol_kg: "",
    zinc_mg_kg: "",
    boron_mg_kg: "",
    iron_mg_kg: "",
    manganese_mg_kg: "",
    copper_mg_kg: "",
  });

  const [flagOverrides, setFlagOverrides] = useState({
    is_acidic: { enabled: false, value: false },
    is_saline_sodic: { enabled: false, value: false },
    is_calcareous: { enabled: false, value: false },
    heavy_metals_detected: { enabled: true, value: false },
  });

  const [submitting, setSubmitting] = useState(false);
  const [submitProgress, setSubmitProgress] = useState({ current: 0, total: 0, message: "" });

  /* Load meta + countries */
  useEffect(() => {
    const initData = async () => {
      const token = getToken();
      try {
        const metaRes = await getSoilMasterMetadata(token);
        if (metaRes?.data?.textures?.length > 0) setTexturesList(metaRes.data.textures);
      } catch (e) { console.warn("Using fallback soil metadata", e); }
      try {
        const countryRes = await getCountryAndLanguage(token);
        setCountries(countryRes?.data?.Countries || []);
      } catch (e) { console.error("Failed to load countries:", e); }
    };
    if (isOpen) initData();
  }, [isOpen]);

  /* Reset / prefill */
  useEffect(() => {
    if (!isOpen) return;
    setMessage(null);

    if (recordToEdit) {
      setTargetLevel(recordToEdit.level_type || 2);
      setSelectedCountryId(recordToEdit.geography?.CountryID || "");
      setFormData({
        texture: recordToEdit.texture || "Loam",
        bulk_density_g_cm3: recordToEdit.bulk_density_g_cm3 ?? "",
        ph: recordToEdit.ph !== null && recordToEdit.ph !== undefined ? String(recordToEdit.ph) : "",
        ec_ds_m: recordToEdit.ec_ds_m !== null && recordToEdit.ec_ds_m !== undefined ? String(recordToEdit.ec_ds_m) : "",
        organic_carbon_pct: recordToEdit.organic_carbon_pct !== null && recordToEdit.organic_carbon_pct !== undefined ? String(recordToEdit.organic_carbon_pct) : "",
        cec_cmol_kg: recordToEdit.cec_cmol_kg ?? "",
        esp_pct: recordToEdit.esp_pct ?? "",
        available_n_kg_ha: recordToEdit.available_n_kg_ha ?? "",
        available_p2o5_kg_ha: recordToEdit.available_p2o5_kg_ha ?? "",
        available_k2o_kg_ha: recordToEdit.available_k2o_kg_ha ?? "",
        sulfur_s_mg_kg: recordToEdit.sulfur_s_mg_kg ?? "",
        calcium_cmol_kg: recordToEdit.calcium_cmol_kg ?? "",
        magnesium_cmol_kg: recordToEdit.magnesium_cmol_kg ?? "",
        zinc_mg_kg: recordToEdit.zinc_mg_kg ?? "",
        boron_mg_kg: recordToEdit.boron_mg_kg ?? "",
        iron_mg_kg: recordToEdit.iron_mg_kg ?? "",
        manganese_mg_kg: recordToEdit.manganese_mg_kg ?? "",
        copper_mg_kg: recordToEdit.copper_mg_kg ?? "",
      });
      setFlagOverrides({
        is_acidic: { enabled: true, value: Boolean(recordToEdit.is_acidic) },
        is_saline_sodic: { enabled: true, value: Boolean(recordToEdit.is_saline_sodic) },
        is_calcareous: { enabled: true, value: Boolean(recordToEdit.is_calcareous) },
        heavy_metals_detected: { enabled: true, value: Boolean(recordToEdit.heavy_metals_detected) },
      });
    } else {
      setSelectedCountryId("");
      setSelectedProvinceIds([]);
      setSelectedDistrictIds([]);
      setSelectedCommuneIds([]);
      setSelectedVillageIds([]);
      setTargetLevel(2);
      setFormData({
        texture: "Loam",
        bulk_density_g_cm3: "",
        ph: "6.5",
        ec_ds_m: "1.2",
        organic_carbon_pct: "0.75",
        cec_cmol_kg: "",
        esp_pct: "",
        available_n_kg_ha: "",
        available_p2o5_kg_ha: "",
        available_k2o_kg_ha: "",
        sulfur_s_mg_kg: "",
        calcium_cmol_kg: "",
        magnesium_cmol_kg: "",
        zinc_mg_kg: "",
        boron_mg_kg: "",
        iron_mg_kg: "",
        manganese_mg_kg: "",
        copper_mg_kg: "",
      });
      setFlagOverrides({
        is_acidic: { enabled: false, value: false },
        is_saline_sodic: { enabled: false, value: false },
        is_calcareous: { enabled: false, value: false },
        heavy_metals_detected: { enabled: true, value: false },
      });
    }
  }, [isOpen, recordToEdit]);

  /* Auto-computed */
  const autoFlags = useMemo(() => {
    const phVal = parseFloat(formData.ph);
    const ecVal = parseFloat(formData.ec_ds_m);
    const espVal = parseFloat(formData.esp_pct);
    return {
      is_acidic: !isNaN(phVal) && phVal < 6.5,
      is_saline_sodic: (!isNaN(ecVal) && ecVal >= 4.0) || (!isNaN(espVal) && espVal >= 15.0),
      is_calcareous: !isNaN(phVal) && phVal >= 7.8,
    };
  }, [formData.ph, formData.ec_ds_m, formData.esp_pct]);

  const computedFlags = useMemo(() => ({
    is_acidic: flagOverrides.is_acidic.enabled ? flagOverrides.is_acidic.value : autoFlags.is_acidic,
    is_saline_sodic: flagOverrides.is_saline_sodic.enabled ? flagOverrides.is_saline_sodic.value : autoFlags.is_saline_sodic,
    is_calcareous: flagOverrides.is_calcareous.enabled ? flagOverrides.is_calcareous.value : autoFlags.is_calcareous,
    heavy_metals_detected: flagOverrides.heavy_metals_detected.value,
  }), [autoFlags, flagOverrides]);

  const setFlag = (key, next) => {
    setFlagOverrides((prev) => ({ ...prev, [key]: next }));
  };

  /* Cascade loaders */
  useEffect(() => {
    setSelectedProvinceIds([]);
    setSelectedDistrictIds([]);
    setSelectedCommuneIds([]);
    setSelectedVillageIds([]);
    if (!selectedCountryId) { setProvinces([]); return; }
    const fetchProvinces = async () => {
      setLoadingProvinces(true);
      try {
        const token = getToken();
        const res = await getProvincesByCountry(selectedCountryId, token);
        setProvinces(res?.data || res || []);
      } catch (err) {
        console.error("Error loading provinces:", err);
        toast.error("Failed to load provinces");
        setProvinces([]);
      } finally { setLoadingProvinces(false); }
    };
    fetchProvinces();
  }, [selectedCountryId]);

  useEffect(() => {
    setSelectedDistrictIds([]); setSelectedCommuneIds([]); setSelectedVillageIds([]);
    if (selectedProvinceIds.length === 0) { setDistricts([]); return; }
    const fetchDistricts = async () => {
      setLoadingDistricts(true);
      try {
        const token = getToken();
        const promises = selectedProvinceIds.map((pId) =>
          getDistrictsByProvince(pId, token).then((r) => r?.data || r || []));
        setDistricts((await Promise.all(promises)).flat());
      } catch (err) { console.error("Error loading districts:", err); setDistricts([]); }
      finally { setLoadingDistricts(false); }
    };
    fetchDistricts();
  }, [selectedProvinceIds]);

  useEffect(() => {
    setSelectedCommuneIds([]); setSelectedVillageIds([]);
    if (selectedDistrictIds.length === 0) { setCommunes([]); return; }
    const fetchCommunes = async () => {
      setLoadingCommunes(true);
      try {
        const token = getToken();
        const promises = selectedDistrictIds.map((dId) =>
          getCommunesByDistrict(dId, token).then((r) => r?.data || r || []));
        setCommunes((await Promise.all(promises)).flat());
      } catch (err) { console.error("Error loading communes:", err); setCommunes([]); }
      finally { setLoadingCommunes(false); }
    };
    fetchCommunes();
  }, [selectedDistrictIds]);

  useEffect(() => {
    setSelectedVillageIds([]);
    if (selectedCommuneIds.length === 0) { setVillages([]); return; }
    const fetchVillages = async () => {
      setLoadingVillages(true);
      try {
        const token = getToken();
        const promises = selectedCommuneIds.map((cId) =>
          getVillagesByCommune(cId, token).then((r) => r?.data || r || []));
        setVillages((await Promise.all(promises)).flat());
      } catch (err) { console.error("Error loading villages:", err); setVillages([]); }
      finally { setLoadingVillages(false); }
    };
    fetchVillages();
  }, [selectedCommuneIds]);

  const targetSummary = useMemo(() => {
    switch (targetLevel) {
      case 1: return { ids: selectedCountryId ? [selectedCountryId] : [], name: "Country", ok: !!selectedCountryId };
      case 2: return { ids: selectedProvinceIds, name: "Province", ok: selectedProvinceIds.length > 0 };
      case 3: return { ids: selectedDistrictIds, name: "District", ok: selectedDistrictIds.length > 0 };
      case 4: return { ids: selectedCommuneIds, name: "Commune", ok: selectedCommuneIds.length > 0 };
      case 5: return { ids: selectedVillageIds, name: "Village", ok: selectedVillageIds.length > 0 };
      default: return { ids: [], name: "", ok: false };
    }
  }, [targetLevel, selectedCountryId, selectedProvinceIds, selectedDistrictIds, selectedCommuneIds, selectedVillageIds]);

  const handleInputChange = (field, value) => setFormData((prev) => ({ ...prev, [field]: value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setMessage(null);

    const token = getToken();
    const numeric = (v) => (v !== "" && v !== null && v !== undefined ? Number(v) : null);

    const required = [
      { value: formData.texture, label: "Soil Texture" },
      { value: formData.available_n_kg_ha, label: "Available N (kg/ha)" },
      { value: formData.available_p2o5_kg_ha, label: "Available P₂O₅ (kg/ha)" },
      { value: formData.available_k2o_kg_ha, label: "Available K₂O (kg/ha)" },
    ];
    const missing = required.filter((c) => c.value === "" || c.value === null || c.value === undefined);
    if (missing.length > 0) {
      setMessage({ type: "error", text: `Please fill required field(s): ${missing.map((m) => m.label).join(", ")}.` });
      return;
    }

    const soilPayload = {
      texture: formData.texture ? String(formData.texture).trim() : null,
      bulk_density_g_cm3: numeric(formData.bulk_density_g_cm3),
      ph: numeric(formData.ph),
      ec_ds_m: numeric(formData.ec_ds_m),
      organic_carbon_pct: numeric(formData.organic_carbon_pct),
      available_n_kg_ha: numeric(formData.available_n_kg_ha),
      available_p2o5_kg_ha: numeric(formData.available_p2o5_kg_ha),
      available_k2o_kg_ha: numeric(formData.available_k2o_kg_ha),
      sulfur_s_mg_kg: numeric(formData.sulfur_s_mg_kg),
      calcium_cmol_kg: numeric(formData.calcium_cmol_kg),
      magnesium_cmol_kg: numeric(formData.magnesium_cmol_kg),
      zinc_mg_kg: numeric(formData.zinc_mg_kg),
      boron_mg_kg: numeric(formData.boron_mg_kg),
      iron_mg_kg: numeric(formData.iron_mg_kg),
      manganese_mg_kg: numeric(formData.manganese_mg_kg),
      copper_mg_kg: numeric(formData.copper_mg_kg),
      cec_cmol_kg: numeric(formData.cec_cmol_kg),
      esp_pct: numeric(formData.esp_pct),
      is_acidic: computedFlags.is_acidic,
      is_saline_sodic: computedFlags.is_saline_sodic,
      is_calcareous: computedFlags.is_calcareous,
      heavy_metals_detected: computedFlags.heavy_metals_detected,
    };

    if (recordToEdit) {
      try {
        setSubmitting(true);
        const res = await updateGeographicalSoil(recordToEdit.id, soilPayload, token);
        toast.success(res?.message || "Soil record updated successfully!");
        onSuccess?.();
        onClose();
      } catch (err) {
        console.error("Update error:", err);
        setMessage({ type: "error", text: err?.response?.data?.message || err.message || "Failed to update record" });
      } finally { setSubmitting(false); }
      return;
    }

    if (!targetSummary.ok) {
      setMessage({ type: "error", text: `Please select at least one ${targetSummary.name} at Level ${targetLevel}.` });
      return;
    }

    const targetIds = targetSummary.ids;
    setSubmitting(true);
    setSubmitProgress({ current: 0, total: targetIds.length, message: "Starting insertions..." });

    let successCount = 0, conflictCount = 0, errorCount = 0;
    const errorsList = [];

    const insertOne = async (levelId) => {
      try {
        await createGeographicalSoil({ ...soilPayload, level_type: targetLevel, level_id: levelId }, token);
        return { status: "success" };
      } catch (err) {
        if (err.response?.status === 409) return { status: "conflict" };
        return { status: "error", error: err.response?.data?.message || err.message };
      }
    };

    const chunkSize = 4;
    for (let i = 0; i < targetIds.length; i += chunkSize) {
      const chunk = targetIds.slice(i, i + chunkSize);
      setSubmitProgress({
        current: i, total: targetIds.length,
        message: `Processing ${i + 1} to ${Math.min(i + chunkSize, targetIds.length)} of ${targetIds.length}...`,
      });
      const results = await Promise.all(chunk.map(insertOne));
      results.forEach((r) => {
        if (r.status === "success") successCount++;
        else if (r.status === "conflict") conflictCount++;
        else { errorCount++; errorsList.push(r.error); }
      });
    }

    setSubmitting(false);

    if (successCount > 0) toast.success(`Successfully created ${successCount} soil master record(s)!`);
    if (conflictCount > 0) toast.warn(`${conflictCount} record(s) already existed and were skipped.`);
    if (errorCount > 0) toast.error(`${errorCount} failed. Error: ${errorsList[0] || "Unknown error"}`);

    if (successCount > 0) { onSuccess?.(); onClose(); }
  };

  const levelOptions = [
    { level: 1, name: "Country" },
    { level: 2, name: "Province" },
    { level: 3, name: "District" },
    { level: 4, name: "Commune" },
    { level: 5, name: "Village" },
  ];

  return (
    <>
      <style>{customStyles}</style>

      <Drawer open={isOpen} direction="right" size={900} style={{ zIndex: 1050 }} className="add-soil-drawer">
        <button
          type="button"
          onClick={onClose}
          disabled={submitting}
          style={{
            border: 'none', color: '#000', padding: '15px',
            position: 'absolute', top: 0, right: 0,
            background: 'transparent', cursor: 'pointer',
          }}
        >
          <Icon icon="lucide:x" style={{ fontSize: '1.5rem' }} />
        </button>

        <div className="card-body drawer-content-scroll">
          <h6 className="text-xxl text-primary-light mb-16">
            {recordToEdit ? 'Edit Geographical Soil Master' : 'Add Geographical Soil Master'}
          </h6>

          {message && (
            <div className={message.type === 'success' ? 'success-message' : 'error-message'}>
              {message.text}
            </div>
          )}

          <form onSubmit={handleSubmit}>
            {/* SECTION 1: GEOGRAPHY */}
            <div className="soil-section">
              <div className="soil-section-title">
                <Icon icon="solar:earth-outline" style={{ fontSize: '20px' }} />
                1. Geography Hierarchy & Target Level
              </div>

              {recordToEdit ? (
                <div className="soil-edit-panel">
                  <div className="row g-2">
                    <div className="col-sm-6">
                      <small className="text-muted d-block">Level Type</small>
                      <span className="badge bg-primary px-2.5 py-1.5 fw-semibold">
                        Level {recordToEdit.level_type}: {recordToEdit.level_name || LEVEL_NAMES[recordToEdit.level_type]}
                      </span>
                    </div>
                    <div className="col-sm-6">
                      <small className="text-muted d-block">Entity Name</small>
                      <span className="fw-bold text-dark">{recordToEdit.entity_name || recordToEdit.level_id}</span>
                    </div>
                    {recordToEdit.geography && (
                      <div className="col-12 mt-2 pt-2 border-top">
                        <small className="text-muted d-block mb-1">Hierarchy</small>
                        <div className="d-flex flex-wrap gap-1 align-items-center small text-secondary">
                          {recordToEdit.geography.CountryName && <span>{recordToEdit.geography.CountryName}</span>}
                          {recordToEdit.geography.ProvinceName && (<><span>&gt;</span><span>{recordToEdit.geography.ProvinceName}</span></>)}
                          {recordToEdit.geography.DistrictName && (<><span>&gt;</span><span>{recordToEdit.geography.DistrictName}</span></>)}
                          {recordToEdit.geography.CommuneName && (<><span>&gt;</span><span>{recordToEdit.geography.CommuneName}</span></>)}
                          {recordToEdit.geography.VillageName && (<><span>&gt;</span><span>{recordToEdit.geography.VillageName}</span></>)}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                <>
                  <label className="form-label fw-semibold">Apply Soil Master Data To Level:</label>
                  <div className="soil-level-chips">
                    {levelOptions.map((lvl) => (
                      <div
                        key={lvl.level}
                        className={`soil-level-chip ${targetLevel === lvl.level ? 'active' : ''}`}
                        onClick={() => setTargetLevel(lvl.level)}
                      >
                        <input type="radio" name="targetLevel" id={`lvl-${lvl.level}`}
                          checked={targetLevel === lvl.level}
                          onChange={() => setTargetLevel(lvl.level)} />
                        <label htmlFor={`lvl-${lvl.level}`} style={{ cursor: 'pointer', margin: 0 }}>
                          Level {lvl.level} — {lvl.name}
                        </label>
                      </div>
                    ))}
                  </div>

                  <div className="mb-20">
                    <label className="form-label">Country <span className="text-danger-600">*</span></label>
                    <select className="form-select" value={selectedCountryId}
                      onChange={(e) => setSelectedCountryId(e.target.value)}>
                      <option value="">-- Select Country --</option>
                      {countries.map((c) => (
                        <option key={c.ID} value={c.ID}>
                          {c.CountryName} {c.CountryCode ? `(${c.CountryCode})` : ""}
                        </option>
                      ))}
                    </select>
                  </div>

                  {targetLevel >= 2 && (
                    <div className="mb-20">
                      <MultiSelect
                        label="Province / State (Multi Select)"
                        placeholder={!selectedCountryId ? "Select a country first" : "-- Select Province(s) --"}
                        items={provinces}
                        selectedIds={selectedProvinceIds}
                        onChange={setSelectedProvinceIds}
                        idKey="ID" nameKey="ProvinceName"
                        disabled={!selectedCountryId} loading={loadingProvinces}
                        emptyMessage="No provinces found for this country"
                        helperText="Select one or more provinces"
                      />
                    </div>
                  )}

                  {targetLevel >= 3 && (
                    <div className="mb-20">
                      <MultiSelect
                        label="District (Multi Select)"
                        placeholder={!selectedProvinceIds.length ? "Select province(s) first" : "-- Select District(s) --"}
                        items={districts}
                        selectedIds={selectedDistrictIds}
                        onChange={setSelectedDistrictIds}
                        idKey="ID" nameKey="DistrictName"
                        disabled={selectedProvinceIds.length === 0} loading={loadingDistricts}
                        emptyMessage="No districts found"
                        helperText="Select one or more districts"
                      />
                    </div>
                  )}

                  {targetLevel >= 4 && (
                    <div className="mb-20">
                      <MultiSelect
                        label="Commune (Multi Select)"
                        placeholder={!selectedDistrictIds.length ? "Select district(s) first" : "-- Select Commune(s) --"}
                        items={communes}
                        selectedIds={selectedCommuneIds}
                        onChange={setSelectedCommuneIds}
                        idKey="ID" nameKey="CommuneName"
                        disabled={selectedDistrictIds.length === 0} loading={loadingCommunes}
                        emptyMessage="No communes found"
                        helperText="Select one or more communes"
                      />
                    </div>
                  )}

                  {targetLevel >= 5 && (
                    <div className="mb-20">
                      <MultiSelect
                        label="Village (Multi Select)"
                        placeholder={!selectedCommuneIds.length ? "Select commune(s) first" : "-- Select Village(s) --"}
                        items={villages}
                        selectedIds={selectedVillageIds}
                        onChange={setSelectedVillageIds}
                        idKey="ID" nameKey="VillageName"
                        disabled={selectedCommuneIds.length === 0} loading={loadingVillages}
                        emptyMessage="No villages found"
                        helperText="Select one or more villages"
                      />
                    </div>
                  )}

                  <div
                    className="d-flex align-items-center justify-content-between flex-wrap gap-2"
                    style={{
                      background: targetSummary.ok ? '#e8f0fe' : '#f1f5f9',
                      border: `1px solid ${targetSummary.ok ? '#c7dbfb' : '#e2e8f0'}`,
                      borderRadius: 8, padding: '10px 12px',
                    }}
                  >
                    <span style={{ fontSize: '0.82rem', color: targetSummary.ok ? '#1a3d7c' : '#64748b' }}>
                      {targetSummary.ok ? (
                        <>
                          <strong>Action Summary:</strong> Saving will create{" "}
                          <strong className="text-primary">{targetSummary.ids.length}</strong>{" "}
                          {targetSummary.name}(s) at <strong>Level {targetLevel} ({LEVEL_NAMES[targetLevel]})</strong>.
                        </>
                      ) : (
                        <>Please select at least one {targetSummary.name || LEVEL_NAMES[targetLevel]}.</>
                      )}
                    </span>
                    <span className={`badge ${targetSummary.ok ? "bg-primary" : "bg-secondary"}`}>
                      {targetSummary.ok ? `${targetSummary.ids.length} entries` : "Incomplete"}
                    </span>
                  </div>
                </>
              )}
            </div>

            {/* SECTION 2: PHYSICAL & CHEMICAL */}
            <div className="soil-section">
              <div className="soil-section-title">
                <Icon icon="solar:test-tube-minimalistic-outline" style={{ fontSize: '20px' }} />
                2. Physical & Chemical Soil Properties
              </div>

              <div className="form-grid-container">
                <div>
                  <label className="form-label">Soil Texture <span className="text-danger-600">*</span></label>
                  <select className="form-select" value={formData.texture}
                    onChange={(e) => handleInputChange("texture", e.target.value)}>
                    <option value="">-- Select Texture --</option>
                    {texturesList.map((t) => (<option key={t} value={t}>{t}</option>))}
                  </select>
                </div>
                <div>
                  <label className="form-label">Bulk Density (g/cm³)</label>
                  <input type="number" step="0.01" className="form-control" placeholder="e.g. 1.35"
                    value={formData.bulk_density_g_cm3}
                    onChange={(e) => handleInputChange("bulk_density_g_cm3", e.target.value)} />
                </div>
              </div>

              <div className="form-grid-3">
                <div>
                  <label className="form-label">pH Level</label>
                  <input type="number" step="0.01" min="0" max="14" className="form-control" placeholder="e.g. 6.5"
                    value={formData.ph}
                    onChange={(e) => handleInputChange("ph", e.target.value)} />
                  <small className="text-muted" style={{ fontSize: '0.72rem' }}>Acidic if &lt; 6.5 | Calcareous if ≥ 7.8</small>
                </div>
                <div>
                  <label className="form-label">EC (dS/m)</label>
                  <input type="number" step="0.01" className="form-control" placeholder="e.g. 1.2"
                    value={formData.ec_ds_m}
                    onChange={(e) => handleInputChange("ec_ds_m", e.target.value)} />
                  <small className="text-muted" style={{ fontSize: '0.72rem' }}>Saline-sodic if ≥ 4.0</small>
                </div>
                <div>
                  <label className="form-label">Organic Carbon (%)</label>
                  <input type="number" step="0.01" className="form-control" placeholder="e.g. 0.75"
                    value={formData.organic_carbon_pct}
                    onChange={(e) => handleInputChange("organic_carbon_pct", e.target.value)} />
                </div>
              </div>

              <div className="form-grid-container">
                <div>
                  <label className="form-label">CEC (cmol/kg)</label>
                  <input type="number" step="0.01" className="form-control" placeholder="Cation Exchange Capacity"
                    value={formData.cec_cmol_kg}
                    onChange={(e) => handleInputChange("cec_cmol_kg", e.target.value)} />
                </div>
                <div>
                  <label className="form-label">ESP (%)</label>
                  <input type="number" step="0.01" className="form-control" placeholder="Exchangeable Sodium %"
                    value={formData.esp_pct}
                    onChange={(e) => handleInputChange("esp_pct", e.target.value)} />
                  <small className="text-muted" style={{ fontSize: '0.72rem' }}>Saline-sodic if ≥ 15.0%</small>
                </div>
              </div>
            </div>

            {/* SECTION 3: NUTRIENTS */}
            <div className="soil-section">
              <div className="soil-section-title">
                <Icon icon="solar:leaf-outline" style={{ fontSize: '20px' }} />
                3. Nutrients & Micronutrients
              </div>

              <div className="soil-section-subtitle">Primary Nutrients (kg/ha) — Required</div>
              <div className="form-grid-3">
                <div>
                  <label className="form-label">Available N (kg/ha) <span className="text-danger-600">*</span></label>
                  <input type="number" step="0.01" required className="form-control" placeholder="Nitrogen"
                    value={formData.available_n_kg_ha}
                    onChange={(e) => handleInputChange("available_n_kg_ha", e.target.value)} />
                </div>
                <div>
                  <label className="form-label">Available P₂O₅ (kg/ha) <span className="text-danger-600">*</span></label>
                  <input type="number" step="0.01" required className="form-control" placeholder="Phosphorus"
                    value={formData.available_p2o5_kg_ha}
                    onChange={(e) => handleInputChange("available_p2o5_kg_ha", e.target.value)} />
                </div>
                <div>
                  <label className="form-label">Available K₂O (kg/ha) <span className="text-danger-600">*</span></label>
                  <input type="number" step="0.01" required className="form-control" placeholder="Potassium"
                    value={formData.available_k2o_kg_ha}
                    onChange={(e) => handleInputChange("available_k2o_kg_ha", e.target.value)} />
                </div>
              </div>

              <div className="soil-section-subtitle">Secondary & Micronutrients</div>
              <div className="form-grid-4">
                <div><label className="form-label">Sulfur S (mg/kg)</label>
                  <input type="number" step="0.01" className="form-control"
                    value={formData.sulfur_s_mg_kg}
                    onChange={(e) => handleInputChange("sulfur_s_mg_kg", e.target.value)} /></div>
                <div><label className="form-label">Calcium Ca (cmol/kg)</label>
                  <input type="number" step="0.01" className="form-control"
                    value={formData.calcium_cmol_kg}
                    onChange={(e) => handleInputChange("calcium_cmol_kg", e.target.value)} /></div>
                <div><label className="form-label">Magnesium Mg (cmol/kg)</label>
                  <input type="number" step="0.01" className="form-control"
                    value={formData.magnesium_cmol_kg}
                    onChange={(e) => handleInputChange("magnesium_cmol_kg", e.target.value)} /></div>
                <div><label className="form-label">Zinc Zn (mg/kg)</label>
                  <input type="number" step="0.01" className="form-control"
                    value={formData.zinc_mg_kg}
                    onChange={(e) => handleInputChange("zinc_mg_kg", e.target.value)} /></div>
                <div><label className="form-label">Boron B (mg/kg)</label>
                  <input type="number" step="0.01" className="form-control"
                    value={formData.boron_mg_kg}
                    onChange={(e) => handleInputChange("boron_mg_kg", e.target.value)} /></div>
                <div><label className="form-label">Iron Fe (mg/kg)</label>
                  <input type="number" step="0.01" className="form-control"
                    value={formData.iron_mg_kg}
                    onChange={(e) => handleInputChange("iron_mg_kg", e.target.value)} /></div>
                <div><label className="form-label">Manganese Mn (mg/kg)</label>
                  <input type="number" step="0.01" className="form-control"
                    value={formData.manganese_mg_kg}
                    onChange={(e) => handleInputChange("manganese_mg_kg", e.target.value)} /></div>
                <div><label className="form-label">Copper Cu (mg/kg)</label>
                  <input type="number" step="0.01" className="form-control"
                    value={formData.copper_mg_kg}
                    onChange={(e) => handleInputChange("copper_mg_kg", e.target.value)} /></div>
              </div>
            </div>

            {/* SECTION 4: CONDITIONS */}
            <div className="soil-section">
              <div className="soil-section-title">
                <Icon icon="solar:shield-warning-outline" style={{ fontSize: '20px' }} />
                4. Soil Conditions & Safety Flags
              </div>

              <div className="form-grid-4">
                <ConditionFlagCard
                  title="Acidic Soil"
                  description="pH < 6.5"
                  autoValue={autoFlags.is_acidic}
                  override={flagOverrides.is_acidic}
                  onChange={(next) => setFlag('is_acidic', next)}
                />
                <ConditionFlagCard
                  title="Saline-Sodic"
                  description="EC ≥ 4.0 or ESP ≥ 15%"
                  autoValue={autoFlags.is_saline_sodic}
                  override={flagOverrides.is_saline_sodic}
                  onChange={(next) => setFlag('is_saline_sodic', next)}
                />
                <ConditionFlagCard
                  title="Calcareous"
                  description="pH ≥ 7.8"
                  autoValue={autoFlags.is_calcareous}
                  override={flagOverrides.is_calcareous}
                  onChange={(next) => setFlag('is_calcareous', next)}
                />
                <ConditionFlagCard
                  title="Heavy Metals"
                  description="Toxicity / Contamination"
                  autoValue={false}
                  override={flagOverrides.heavy_metals_detected}
                  onChange={(next) => setFlag('heavy_metals_detected', next)}
                  isManualOnly
                />
              </div>
            </div>

            {/* Progress bar */}
            {submitting && submitProgress.total > 1 && (
              <div className="p-3 bg-light rounded border mb-4">
                <div className="d-flex justify-content-between align-items-center mb-1 small fw-semibold">
                  <span>{submitProgress.message}</span>
                  <span>{Math.round((submitProgress.current / submitProgress.total) * 100)}%</span>
                </div>
                <div className="progress" style={{ height: '8px' }}>
                  <div className="progress-bar progress-bar-striped progress-bar-animated bg-primary"
                    role="progressbar"
                    style={{ width: `${(submitProgress.current / submitProgress.total) * 100}%` }} />
                </div>
              </div>
            )}

            {/* Footer */}
            <div className="drawer-footer">
              <button type="button" className="border border-danger-600 text-danger-600 px-56 py-10 radius-8"
                onClick={onClose} disabled={submitting}>
                Cancel
              </button>
              <button type="submit" className="btn btn-yellow px-56 py-12" disabled={submitting}>
                {submitting ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" role="status" />
                    Saving...
                  </>
                ) : recordToEdit ? "Update Soil Profile" : `Save Soil Data (${targetSummary.ids.length || 0} entries)`}
              </button>
            </div>
          </form>
        </div>
      </Drawer>
    </>
  );
};

export default AddGeographicalSoil;